import React, { useState } from 'react';
import Modal from './Modal';
import TyreDetails from './TyreDetails';
import { getRemainingContent } from '../utils/textUtils';
import { TyreLabel, ApprovalBadge, SEASON } from './TyreLabel';
import '../scss/TyreSegments.scss';

const TyreSegments = ({ segments, loading, error }) => {
	const [selectedTyre, setSelectedTyre] = useState(null);
	const [isModalOpen, setIsModalOpen] = useState(false);
	// Ajout de l'état pour les quantités de chaque pneu
	const [quantities, setQuantities] = useState({});

	if (loading) return <div className="segments-loading">Chargement des segments...</div>;
	if (error) return null; // On cache silencieusement les erreurs des segments
	if (!segments?.length) return null;

	// On limite à 3 segments maximum
	const displayedSegments = segments.slice(0, 3);

	// Fonction pour récupérer la saison traduite
	const getSeasonLabel = (season) => {
		return SEASON[season] || season;
	};

	// Fonction pour gérer le changement de quantité
	const handleQuantityChange = (tyreId, newQuantity) => {
		let quantity = parseInt(newQuantity) || 2;
		// Assurer que la quantité est paire et >= 2
		if (quantity < 2) quantity = 2;
		if (quantity % 2 !== 0) quantity = quantity + 1; // Arrondir au nombre pair supérieur
		if (quantity > 99) quantity = 98; // Maximum pair
		
		setQuantities(prev => ({
			...prev,
			[tyreId]: quantity
		}));
	};

	// Fonction pour incrémenter/décrémenter la quantité par 2
	const adjustQuantity = (tyreId, delta) => {
		const currentQuantity = quantities[tyreId] || 2;
		const newQuantity = currentQuantity + (delta * 2); // Incrément/décrément par 2
		handleQuantityChange(tyreId, newQuantity);
	};

	// Fonction pour générer un ID unique pour chaque pneu
	const getTyreId = (segment, segmentIndex) => {
		// On utilise l'index du segment + quelques propriétés du pneu pour créer un ID unique
		return `${segmentIndex}-${segment.tyre.brand}-${segment.tyre.profile?.name || 'default'}-${segment.tyre.width}-${segment.tyre.height}-${segment.tyre.radius}`;
	};

	setTimeout(() => {
		if(window.innerWidth < 992) {
			const segmentsGrid = document.querySelector('.segments-grid.swiper');
			if (segmentsGrid instanceof HTMLElement && window.Swiper) {
				new window.Swiper(segmentsGrid, {
					slidesPerView: 1,
					spaceBetween: 20,
					navigation: {
						nextEl: segmentsGrid.querySelector('.swiper-button-next'),
						prevEl: segmentsGrid.querySelector('.swiper-button-prev'),
					},
				});
			}
		}
	});

	return (
		<div className="tyre-segments row align-items-start">
			<div className="tyre-segment-info col-12 col-lg-3">
				<h2 className="h2">Nos recommandations</h2>
				<div>Découvrez notre sélection de pneus adaptés à tous les budgets, du plus économique au premium.</div>
				<a href="/demande-de-rendez-vous" className="btn btn-primary">Prendre rendez-vous</a>
			</div>
			<div className="segments-grid swiper p-0 col-12 col-lg-9">
				<div className="row w-100 m-0 swiper-wrapper">
					{displayedSegments.map((segment, segmentIndex) => {
						const tyreId = getTyreId(segment, segmentIndex);
						return (
							<div key={segment.name} className="segment-card col-12 col-lg-4 swiper-slide">
								<div className="segment-header">
									<h3 className="h3">{segment.name}</h3>
								</div>
								{segment.tyre && (
									<div className="segment-tyre">
										<div className="tyre-image">
											<div className="left-pictos">
												<TyreLabel type="fuel" value={segment.tyre.fuelConsumption} />
												<TyreLabel type="grip" value={segment.tyre.wetGrip} />
												<TyreLabel type="sound" value={segment.tyre.soundLevel + 'dB'}/>
											</div>

											<img
												src={segment.tyre.profile.imageUrl || '/wp-content/plugins/ax_tyres_catalog/app/build/svg/tyre-placeholder.jpg'}
												alt={segment.tyre.description}
											/>
											
											<div className="right-pictos">
												<TyreLabel type={segment.tyre.season} value={SEASON[segment.tyre.season] || segment.tyre.season} />
												<div className="mspmsf">
													{segment.tyre.pmsf && segment.tyre.msMarked && (
														<img src={'/wp-content/plugins/ax_tyres_catalog/app/public/svg/ms-pmsf.svg'} alt="pmsf" />
													)}
													{segment.tyre.pmsf && (
														<img src={'/wp-content/plugins/ax_tyres_catalog/app/public/svg/pmsf.svg'} alt="pmsf" />
													)}
													{segment.tyre.msMarked && (
														<img src={'/wp-content/plugins/ax_tyres_catalog/app/public/svg/ms.svg'} alt="pmsf" />
													)}
												</div>
											</div>
										</div>
										<div className="tyre-info row">
											<div className="tyre-info-left col-7">
												<div className="tyre-brand">
													{segment.tyre.brandLogo ? (
														<img src={segment.tyre.brandLogo} alt={segment.tyre.brand} />
													) : (
														<h4>{segment.tyre.brand}</h4>
													)}
												</div>
												<div className="tyre-description">
													Pneu {SEASON[segment.tyre.season]} {segment.tyre.brand} 
													<div>
														{segment.tyre.profile.name}
													</div>
												</div>
											</div>
											<div className="tyre-info-right col-5">
												<div className="tyre-price">
													<span className="price">
														{segment.tyre.formattedPrice}*
													</span>
													{segment.tyre.isDiscount && (
														<span className="public-price">
															{segment.tyre.formattedPublicPrice}
														</span>
													)}
													<span className="price-notice">
														*Prix unitaire TTC, exclusif internet
													</span>
												</div>
											</div>
											<div className="tyre-specs-container col-12">
												<div className="tyre-specs">
													<span>
														{segment.tyre.width}/{segment.tyre.height} R{segment.tyre.radius} {segment.tyre.loadIndex} {segment.tyre.speedIndex}
														{segment.tyre.reinforced && (
															<TyreLabel type="reinforced" value={segment.tyre.reinforced} />
														)}
														{segment.tyre.approvalLabel && (
															<ApprovalBadge label={segment.tyre.approvalLabel} />
														)}
														{/* Labels spéciaux */}
														{segment.tyre.crm && (
															<TyreLabel type="feature" value="crm" />
														)}
														{segment.tyre.continentalSeal && (
															<TyreLabel type="feature" value="continentalSeal" />
														)}
														{segment.tyre.continentalSilent && (
															<TyreLabel type="feature" value="continentalSilent" />
														)}
														{segment.tyre.ev && (
															<TyreLabel type="feature" value="ev" />
														)}
														{segment.tyre.runflatType && (
															<TyreLabel type="feature" value={segment.tyre.runflatType} />
														)}
													</span>
												</div>
											</div>
										</div>
										<div className="tyre-actions">
											<div className="quantity-selector">
												<button 
													className="quantity-btn minus"
													onClick={() => adjustQuantity(segmentIndex, -1)}
													disabled={(quantities[segmentIndex] || 2) <= 2}
												>
													−
												</button>
												<input
													type="number"
													min="2"
													max="98"
													step="2"
													value={quantities[segmentIndex] || 2}
													onChange={(e) => handleQuantityChange(segmentIndex, e.target.value)}
													className="quantity-input"
												/>
												<button 
													className="quantity-btn plus"
													onClick={() => adjustQuantity(segmentIndex, 1)}
													disabled={(quantities[segmentIndex] || 2) >= 98}
												>
													+
												</button>
											</div>
											<button
												className="btn btn-primary quote-btn"
												onClick={() => {
													setSelectedTyre({
														...segment.tyre,
														quantity: quantities[segmentIndex] || 2
													});
													setIsModalOpen(true);
												}}
											>
												Obtenir mon devis
											</button>
										</div>
									</div>
								)}
							</div>
						);
					})}
				</div>
				<div className="swiper-button-next"></div>
				<div className="swiper-button-prev"></div>
			</div>

			<Modal
				isOpen={isModalOpen}
				onClose={() => setIsModalOpen(false)}
				title="Détails du pneu"
				tyreData={selectedTyre}
				remainingDescription={getRemainingContent(selectedTyre?.profile?.description)}
			>
				{selectedTyre && <TyreDetails tyre={selectedTyre} />}
			</Modal>
		</div>
	);
};

export default TyreSegments; 